import { Alert, Stack, Text, Title } from '@mantine/core';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { RequestableGroupSelector } from '@/components/service-request/RequestableGroupSelector';
import {
  listRequestableGroups,
  type RequestableGroupProjection,
} from '@/lib/serviceRequestOriginatorService';
import type { IntakeAction, IntakeAnswers } from '../types';

interface StepGroupSelectionProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  organizationId?: string;
}

export function StepGroupSelection({
  answers,
  dispatch,
  organizationId,
}: StepGroupSelectionProps): JSX.Element {
  const medplum = useMedplum();
  const [groups, setGroups] = useState<RequestableGroupProjection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    void listRequestableGroups(medplum, organizationId)
      .then((result) => {
        if (!cancelled) setGroups(result.groups);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setGroups([]);
          setError(err instanceof Error ? err.message : 'Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, organizationId]);

  const value =
    answers.groupSelection === 'help-find'
      ? 'help-find'
      : answers.requestedGroupId ?? null;

  const handleChange = (next: string | 'help-find'): void => {
    if (next === 'help-find') {
      dispatch({
        type: 'SET_GROUP_THERAPY_SELECTION',
        payload: {
          groupSelection: 'help-find',
          requestedGroupId: undefined,
          requestedGroupTemplateId: undefined,
        },
      });
      return;
    }
    const group = groups.find((candidate) => candidate.groupId === next);
    dispatch({
      type: 'SET_GROUP_THERAPY_SELECTION',
      payload: {
        groupSelection: 'group',
        requestedGroupId: next,
        requestedGroupTemplateId: group?.groupTemplateId,
        requestedGroupName: group?.name,
        modality: group?.modality,
      },
    });
  };

  return (
    <Stack gap="md">
      <div>
        <Title order={3}>Choose a Group</Title>
        <Text size="sm" c="dimmed" mt={4}>
          Select a specific Group or ask our team to help find the right fit.
        </Text>
      </div>
      {error ? (
        <Alert color="red" title="Groups could not be loaded">
          {error}
        </Alert>
      ) : null}
      <RequestableGroupSelector
        groups={groups}
        value={value}
        onChange={handleChange}
        loading={loading}
      />
    </Stack>
  );
}
